import Link from 'next/link';
import { useState } from 'react';
import { FacilityBotLogo } from '../../components/Svg';
import {
	icon,
	risk_profile,
	product_security,
	reports,
	data_security,
	app_security,
	access_control,
	infrastructure,
	security_grades,
	endpoint_security,
	network_security,
	policies,
	customer_data,
} from '../../utils/images';

import { pluralize } from '../../utils';
import { ASSET_BASE_URL } from '../../constants';
import { Layout } from '../../components';
import { Row, Col, Card, Modal, Typography, Space, Tag, Flex, Divider, List } from 'antd';
import { CheckCircleFilled, SyncOutlined, ExportOutlined, StarFilled } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

export default function SecurityStatus() {
	const [selected, setSelected] = useState(0);
	const [modalVisible, setModalVisible] = useState(false);

	const handleCardClick = (index) => {
		setSelected(index);
		setModalVisible(true);
	};

	return (
		<Layout pageName="Security Status">
			<div className="security-status">
				<section className="heading-container">
					<Flex justify="center">
						<Title level={1} className="heading-title">
							Security Status
						</Title>
					</Flex>
				</section>

				<section className="security-section">
					<div className="container">
						<Row gutter={[16, 16]}>
							{SECURITY_STATUS.map(({ title, icon: statusIcon, checklists }, index) => (
								<Col xs={24} sm={12} md={8} key={index}>
									<Card
										hoverable
										className="status-card"
										onClick={() => handleCardClick(index)}
										title={
											<Space align="center">
												<img src={statusIcon} alt={title} className="status-card__icon" />
												<Title level={5} style={{ margin: 0 }}>
													{title}
												</Title>
											</Space>
										}
									>
										<Flex vertical gap={8}>
											{checklists.slice(0, 3).map(({ title: itemTitle, in_progress }, idx) => (
												<Flex key={idx} justify="space-between" align="center">
													<Text ellipsis className="status-card__item-title">
														{itemTitle}
													</Text>
													{in_progress ? (
														<SyncOutlined spin className="status-icon status-icon--success" />
													) : (
														<CheckCircleFilled className="status-icon status-icon--success" />
													)}
												</Flex>
											))}

											{checklists.length > 3 && (
												<Flex justify="center">
													<Text strong className="status-card__view-more">
														View {checklists.length - 3} more {pluralize(checklists.length - 3, 'item')}
													</Text>
												</Flex>
											)}
										</Flex>
									</Card>
								</Col>
							))}
						</Row>

						<Divider />

						<Flex justify="center" align="center" gap={12} className="security-footer">
							<Text>Powered By</Text>
							<Link href="/">
								<FacilityBotLogo />
							</Link>
						</Flex>
					</div>
				</section>

				<StatusDetailModal
					status={SECURITY_STATUS[selected]}
					visible={modalVisible}
					onClose={() => setModalVisible(false)}
				/>
			</div>
		</Layout>
	);
}

const StatusDetailModal = ({ status, visible, onClose }) => {
	const { title, icon: statusIcon, checklists, extra_content } = status;

	return (
		<Modal
			open={visible}
			onCancel={onClose}
			footer={null}
			width={800}
			centered
			zIndex={1100}
			title={
				<Space align="center">
					<img src={statusIcon} alt={title} className="modal-header-icon" />
					<Title level={4} style={{ margin: 0 }}>
						{title}
					</Title>
				</Space>
			}
		>
			<Flex vertical gap={16} className="modal-body-scroll">
				{checklists.map(
					(
						{ title: itemTitle, in_progress, plain_text, link, list, innerHtml, custom_obj, extraTitle, highlighted },
						index,
					) => (
						<Card
							key={index}
							size="small"
							className={`modal-status-card ${highlighted ? 'modal-status-card--highlighted' : ''}`}
						>
							<Flex vertical gap={12}>
								<Flex justify="space-between" align="flex-start" wrap="wrap" gap={8}>
									<Space align="center">
										{in_progress ? (
											<SyncOutlined spin className="status-icon status-icon--success" />
										) : (
											<CheckCircleFilled className="status-icon status-icon--success" />
										)}
										<Text strong style={{ fontSize: 15 }}>
											{itemTitle}
											{extraTitle && <span> - {extraTitle}</span>}
										</Text>
									</Space>

									{in_progress && <Tag color="success">In Progress</Tag>}
								</Flex>

								<div className="modal-status-card__content">
									{plain_text && <Paragraph>{plain_text}</Paragraph>}

									{innerHtml && (
										<Paragraph>
											<span dangerouslySetInnerHTML={{ __html: innerHtml }} />
										</Paragraph>
									)}

									{link && (
										<Paragraph>
											<a href={link} target="_blank" rel="noreferrer" className="status-link">
												{link}
											</a>
										</Paragraph>
									)}

									{list && (
										<List
											size="small"
											dataSource={list}
											renderItem={(item) => (
												<List.Item className="status-list-item">
													<Text>• {item}</Text>
												</List.Item>
											)}
										/>
									)}

									{custom_obj && (
										<Card size="small" className="custom-obj-card">
											<Title level={5} style={{ marginBottom: 12 }}>
												{custom_obj.text}
											</Title>
											<List
												dataSource={custom_obj.list}
												renderItem={(item) => (
													<List.Item className="custom-list-item">
														<Row style={{ width: '100%' }} align="middle">
															<Col span={2}>
																<a href={item.link} title="View Test" target="_blank" rel="noreferrer">
																	<ExportOutlined className="status-icon status-icon--highlight" />
																</a>
															</Col>
															<Col span={18}>
																<Text strong>{item.website}</Text>
															</Col>
															<Col span={4}>
																<Text strong className="custom-obj-score">
																	Grade {item.score}
																</Text>
															</Col>
														</Row>
													</List.Item>
												)}
											/>
										</Card>
									)}
								</div>
							</Flex>
						</Card>
					),
				)}

				{extra_content && (
					<Card size="small">
						<Paragraph style={{ margin: 0 }}>
							<span dangerouslySetInnerHTML={{ __html: extra_content.innerHtml }} />
						</Paragraph>
					</Card>
				)}
			</Flex>
		</Modal>
	);
};

// SecurityStatus Static Data
const SECURITY_STATUS = [
	{
		title: 'Risk Profile',
		icon: risk_profile,
		checklists: [
			{
				title: 'Data Access Level',
				plain_text:
					'FacilityBot stores the minimum amount of Personal Data required to perform its functions. In most cases, the only Personal Data stored is the name and messaging platform ID.',
			},
			{
				title: 'Impact Level',
				plain_text:
					'Low to Moderate. Facilitybot stores maintenance-related data such as fault reports and preventive maintenance checklists.',
			},
			{
				title: 'Critical Dependence',
				plain_text: 'In general, a system failure for FacilityBot will not be considered critical by the end client.',
			},
			{
				title: 'Hosting',
				plain_text: 'FacilityBot is hosted using Amazon Web Services.',
			},
		],
	},
	{
		title: 'Product Security',
		icon: product_security,
		checklists: [
			{
				title: 'Role-Based Access Control',
				plain_text: 'Users can be assigned one of the following roles',
				list: [
					'Requestor: Can make requests.',
					'Responder: Can respond to requests and complete checklists',
					'Manager: Can configure settings and view statistics',
					'Admin: In addition to Manager rights, can manage accounts and subscriptions to FacilityBot',
				],
			},
			{
				title: 'Service Level Agreement',
				plain_text:
					'Services will be available 99.9%, measured monthly, excluding holidays and weekends and scheduled maintenance.',
			},
			{
				title: 'Single Sign-On',
				plain_text:
					'We support Single-Sign-On via OAuth and SAML. Platforms supported include Microsoft Azure AD and Okta.',
			},
		],
	},
	{
		title: 'Reports',
		icon: reports,
		checklists: [
			{
				title: 'Network Diagram',
				innerHtml:
					'Please email <a href="mailto:sales@facilitybot.co" style="color: #4aafa8;">sales@facilitybot.co</a> to request access to our network diagram',
			},
			{
				title: 'Security Whitepaper',
				innerHtml:
					'FacilityBot uses this Security Status Page in place of a traditional security whitepaper. If you any additional questions regarding our security controls, please send an email to <a href="mailto:sales@facilitybot.co" style="color: #4aafa8;">sales@facilitybot.co</a>',
			},
		],
	},
	{
		title: 'Data Security',
		icon: data_security,
		checklists: [
			{
				title: 'Access Monitoring',
				plain_text: 'All access attempts to FacilityBot are logged',
			},
			{
				title: 'AWS Backup',
				plain_text: 'All backups and restoration are managed through AWS Backup service.',
				list: [
					'Backup Frequency: Daily automatic backups',
					'Retention Period: 15 days',
					'Restore Testing: Conducted monthly to ensure data integrity and recovery reliability',
				],
				highlighted: true,
			},
			{
				title: 'Data Erasure',
				plain_text: 'Customers may contact our helpdesk for any data deletion requests',
			},
			{
				title: 'Encryption-at-rest',
				plain_text:
					'Data at rest in FacilityBot servers is encrypted including application files, backup files, and databases.',
			},
			{
				title: 'Encryption-in-transit',
				plain_text:
					"All data transmitted between FacilityBot's end users (both portal and messaging services) and FacilityBot's server undergoes encryption using strong encryption protocols.",
			},
			{
				title: 'Physical Security',
				plain_text:
					'Physical security for our data centers are handled by Amazon Web Services. You can find additional details at:',
				list: ['https://aws.amazon.com/compliance/data-center/controls/'],
			},
			{
				title: 'AWS Key Rotation',
				plain_text: 'AWS Keys for user access are rotated every 6 months',
			},
			{
				title: 'Recovery Time Objective (RTO) and Recovery Point Objective (RPO)',
				list: [
					"The system's Recovery Time Objective is 99.9% annual uptime (or a target of less than 8.77 hours of downtime per year)",
					"The system's Recovery Point Objective is 24 hours",
				],
			},
			{
				title: 'Web Application Firewall (WAF) and Intrusion Detection System (IDS)',
				list: [
					'AWS Web Application Firewall (WAF) is deployed. The WAF is utilized as an intrusion detection system with alerts set for abnormal or suspicious requests',
				],
			},
			{
				title: 'Database Activity Monitoring (DAM)',
				list: ['AWS Cloudwatch is deployed for Database Activity Monitoring'],
			},
			{
				title: 'API Gateway',
				list: ['AWS API Gateway is deployed to secure our APIs'],
			},
			{
				title: 'Anti Virus / Malware scanning for file uploads',
				list: ['All file uploads that are stored in our AWS S3 storage are scanned for viruses / malware'],
			},
		],
	},
	{
		title: 'App Security',
		icon: app_security,
		checklists: [
			{
				title: 'Software Development Lifecycle',
				plain_text: 'As a part of our SDLC, we use security controls to allow our developers to securely write code',
				list: [
					'All issues are tracked in a project management tool and are assigned scope and type labels, including those specifically for security issues.',
					'Developers are required to sign all commits with a private key solely used for FacilityBot purposes, and only from company-owned computers.',
					'Application secrets are never stored in plaintext.',
					'Access to our source code repository is limited to the Engineering team. All commits to our source code repository must be signed, and branch protection is enabled, with all code needing management review before deployment.',
				],
			},
			{
				title: 'Vulnerability Assessment and Penetration Test',
				plain_text:
					'An external Vulnerability Assessment and Penetration Test is conducted annually. Internal SAST and security scans are conducted quarterly.',
			},
			{
				in_progress: false,
				title: 'Zero Day Vulnerability Monitoring',
				plain_text:
					'Newly published CVEs are monitored weekly, triaged for relevance, criticality, and impact. Any necessary patches are implemented as soon as possible.',
			},
			{
				title: 'CI/CD Pipeline',
				plain_text:
					'Continuous Integration and Continuous Delivery (CI/CD) pipelines are used for deployment to reduce the risk of vulnerabilities slipping through due to human oversight.',
			},
			{
				title: 'Automated Secrets Pre-Commit Check',
				plain_text:
					'All code is automatically scanned for secrets. This check prevents developers from accidentally pushing secret keys to production.',
			},
			{
				in_progress: false,
				title: 'Credential Management',
				plain_text: 'We use a third-party secrets manager to securely manage our credentials in our infrastructure.',
			},
			{
				in_progress: false,
				title: 'Vulnerability & Patch Management',
				plain_text:
					'We use a dedicated tool for discovering and remediating vulnerabilities in outdated third-party code dependencies.',
			},
		],
	},
	{
		title: 'Access Control',
		icon: access_control,
		checklists: [
			{
				title: 'Access Control',
				list: [
					'Access to customer data is only permitted on an as-needed basis. Access to internal and external systems is granted to our employees using role-based access control. We have onboarding and offboarding procedures to ensure that employees are only given the access they need based on business justification.',
					'Whenever possible, we provide individual accounts for all users.Shared accounts are only used in situations in which provisioning individual accounts are not possible.',
				],
			},
			{
				title: 'Password Security',
				plain_text:
					'Unique accounts and passwords are required for all users. Passwords are kept confidential and not shared with multiple users. All passwords are required to contain alphabets, numbers, special characters, and at least 8 characters.',
			},
			{
				title: 'Two Factor Authentication',
				plain_text:
					'All users can set up Two Factor Authentication to add an extra layer of access security. Once Two Factor Authentication is set up, the user would need to enter a 6-digit code generated from the Google Authenticator App in order to sign in.',
			},
			{
				title: 'Passkeys',
				plain_text: 'FacilityBot Mobile Apps (iOS and Android) allows Passkeys for easier and more secure logins',
			},
			{
				title: 'Session Binding',
				plain_text:
					'Admins can toggle on session binding as an added layer of access security. User sessions can be bound to:',
				list: [
					'Activity Time (sessions will auto log out after 60 minutes of inactivity)',
					'IP Address (sessions will auto log out if the IP Address changes)',
					'Browser (attackers cannot use the authentication token in a different browser)',
				],
			},
			{
				title: 'Logging',
				plain_text: 'We leverage Amazon Web Services logging capabilities to track activity in our infrastructure.',
			},
		],
	},
	{
		title: 'Infrastructure',
		icon: infrastructure,
		checklists: [
			{
				title: 'Anti-DDoS',
				plain_text: 'We use AWS Shield for anti-DDoS protection.',
			},
			{
				title: 'Bot Control',
				plain_text: 'AWS WAF Bot Control is used to protect against malicious bots.',
			},
			{
				title: 'Server Access',
				plain_text:
					'Access is only granted to the senior engineering team. All logins are protected with 2-factor authentication. Connection to servers via SSH requires a VPN Connection.',
			},
			{
				title: 'Server Hardening',
				plain_text:
					"FacilityBot's infrastructure is deployed on hardened OS images. Multiple hardening practices are implemented including disabling unnecessary ports and removing default passwords.",
			},
			{
				title: 'Separate Production Environment',
				plain_text:
					'We use a testing environment that is completely separated from our production environment. Production user data is never used in our testing environment.',
			},
			{
				title: 'Virtual Private Cloud',
				plain_text: 'All data is stored within a Virtual Private Cloud private subnet.',
			},
			{
				title: 'Operational Monitoring and Anomaly Detection',
				plain_text: 'We use AWS Cloudwatch for operational monitoring and anomaly detection.',
			},
			{
				title: 'Logging and Monitoring',
				plain_text:
					'We currently use Kibana + Elasticsearch for transaction monitoring and instance metrics. Logstash is used for log collection.',
			},
		],
	},
	{
		title: 'Security Certifications',
		icon: security_grades,
		checklists: [
			{
				title: 'Cyber Essentials Mark',
				innerHtml: `FacilityBot Pte Ltd has been assessed and found to conform to Cyber Security Agency of Singapore Cybersecurity Certification - Cyber Essentials Mark (2025) for ICT Vendors <br /> (see <a target="_blank" href="${ASSET_BASE_URL}/FacilityBotPteLtd-CEMCertJuly2026.pdf">Certificate</a>)`,
			},
			{
				title: 'Qualys SSL Labs',
				extraTitle:
					'FacilityBot has implemented TLS 1.2 with AES 256 Cipher, achieving Grade A for our SSL implementation',
				custom_obj: {
					text: 'Endpoints',
					list: [
						{
							website: 'Main Web Portal',
							score: 'A',
							link: 'https://www.ssllabs.com/ssltest/analyze.html?d=portal.facilitybot.co',
						},
					],
				},
			},
			{
				title: 'ISO and CSA Star Certified',
				innerHtml:
					'FacilityBot\'s servers are hosted in AWS which is ISO and CSA Star Certified <br /> (see <a target="_blank" href="https://aws.amazon.com/compliance/iso-certified/">https://aws.amazon.com/compliance/iso-certified/</a>)',
			},
			{
				title: ' SOC Certified',
				innerHtml:
					'FacilityBot\'s servers are hosted in AWS which is SOC Certified <br /> (see <a target="_blank" href="https://aws.amazon.com/compliance/soc-faqs/">https://aws.amazon.com/compliance/soc-faqs/</a>)',
			},
		],
	},
	{
		title: 'Endpoint Security',
		icon: endpoint_security,
		checklists: [
			{
				title: 'Endpoint Detection - Anti Virus and Anti Malware',
				plain_text: 'All devices are protected using macOS XProtect or Windows Defender. Scans are performed monthly',
			},
			{
				in_progress: false,
				title: 'Device Hardening',
				plain_text: 'Critical devices are hardened based on globally recognized (CIS) standards',
			},
			{
				title: 'Mobile Device Management',
				in_progress: false,
			},
		],
	},
	{
		title: 'Network Security',
		icon: network_security,
		checklists: [
			{
				title: 'Spoofing Protection',
				plain_text:
					'Our domain facilitybot.co is configured with SPF, DKIM and DMARC to protect against email spoofing.',
			},
		],
	},
	{
		title: 'Policies',
		icon: policies,
		checklists: [
			{
				title: 'Acceptable Use Policy',
			},
			{
				title: 'Incident Management Program',
			},
			{
				title: 'Disaster Recovery Plan',
			},
			{
				title: 'Secure Coding Practices',
			},
			{
				title: 'OWASP Top 10 Awareness Training',
				plain_text: 'All developers are required to undergo OWASP Top 10 Awareness Training.',
			},
		],
		extra_content: {
			innerHtml:
				'Please email <a href="mailto:sales@facilitybot.co" style="color: #4aafa8;">sales@facilitybot.co</a> to request access to the policies',
		},
	},
	{
		title: 'Customer Data',
		icon: customer_data,
		checklists: [
			{
				title: 'Privacy Policy',
				link: 'https://blog.facilitybot.co/privacy-policy/',
			},
			{
				title: 'Terms and Conditions',
				link: 'https://blog.facilitybot.co/software-service-agreement/',
			},
		],
	},
];
